import React, { Component } from 'react';
import propTypes from 'prop-types';
import get from 'lodash/get';
import { Collapse, Input, Label, Popover, PopoverBody } from 'reactstrap';

import commonHelper from '@das/canvas-common/src/helper';

import AttachChildInput from 'app/CanvasWidgets/components/AttachChildInput';
import { isExperiment } from 'app/Experiments/utils';
import History from 'app/History/components/History';
import ImageSize from 'app/ImageSize/components/ImageSize';
import NameAndTags from 'app/NameAndTags/container';
import { showNoShowField, showServerSideField, variantsField } from 'lib/contract/experiment';
import { noop } from 'lib/noop';
import { SectionHeader } from 'ui/Components';
import { renderArrowStyle } from 'ui/helpers';
import { makeId } from 'utils/selectors';

import styles from './ExperimentForm.css';

/**
 * render a form to create an experiment component
 * @returns {Component} experiment form
 */
export default class ExperimentForm extends Component {
  static propTypes = {
    boundOpenComponentForEditing: propTypes.func,
    componentDetails: propTypes.object,
    isFavorite: propTypes.bool,
    isNewComponent: propTypes.bool,
    isReadonly: propTypes.bool,
    modifyComponent: propTypes.func,
    onNameChange: propTypes.func,
    toggleFavoriteContent: propTypes.func
  };

  static defaultProps = {
    boundOpenComponentForEditing: noop,
    componentDetails: {},
    isFavorite: false,
    isNewComponent: false,
    isReadonly: false,
    modifyComponent: noop,
    onNameChange: noop,
    toggleFavoriteContent: undefined
  };

  constructor(props) {
    super(props);

    this.state = {
      isServerSidePopoverOpen: false,
      showExperimentComponents: false,
      showHistory: false,
      showImageSizeSection: false
    };

    this.changeItemsOrder = this.changeItemsOrder.bind(this);
    this.handleHover = this.handleHover.bind(this);
    this.handleHoverLeave = this.handleHoverLeave.bind(this);
    this.handleInputToggle = this.handleInputToggle.bind(this);
    this.handleWeightSectionClick = this.handleWeightSectionClick.bind(this);
    this.toggleExperimentComponentsSection = this.toggleExperimentComponentsSection.bind(this);
    this.toggleHistorySection = this.toggleHistorySection.bind(this);
    this.togglePopover = this.togglePopover.bind(this);
  }

  handleHover() {
    this.setState({
      isServerSidePopoverOpen: true
    });
  }

  handleHoverLeave() {
    this.setState({
      isServerSidePopoverOpen: false
    });
  }

  /**
   * Handle input toggle flip
   * @param {object} event - click event
   * @param {string} propLocation - location of params
   */
  handleInputToggle(event, propLocation) {
    const { componentDetails, modifyComponent } = this.props;
    const newValue = event.target.checked;

    modifyComponent(newValue, propLocation, componentDetails.Id);
  }

  /**
   * Toggle weight section click
   */
  handleWeightSectionClick() {
    this.setState(ps => ({ showImageSizeSection: !ps.showImageSizeSection }));
  }

  /**
   * toggle experiment components section
   */
  toggleExperimentComponentsSection() {
    this.setState(ps => ({ showExperimentComponents: !ps.showExperimentComponents }));
  }

  /**
   * toggle history section
   */
  toggleHistorySection() {
    this.setState(ps => ({ showHistory: !ps.showHistory }));
  }

  togglePopover() {
    this.setState(ps => ({ isServerSidePopoverOpen: !ps.isServerSidePopoverOpen }));
  }

  /**
   * Repositions the items in the array
   * @param {Array} variantsList - array if variants in experiments
   */
  changeItemsOrder(variantsList) {
    const { componentDetails, modifyComponent } = this.props;
    modifyComponent(variantsList, variantsField.propLocation, componentDetails.Id);
  }

  /**
   * create input checkbox field
   * @param {object} params - function params
   * @param {string} params.name - field name to use as label
   * @param {Function} params.propLocation - property location to update
   * @returns {React.Component} - toggle button
   */
  renderInputCheckboxField = ({ name, propLocation }) => (
    <div className="pl-4">
      <Input
        disabled={this.props.isReadonly}
        id={name}
        className="rounded"
        type="checkbox"
        checked={get(this.props.componentDetails, propLocation, false)}
        onChange={e => this.handleInputToggle(e, propLocation)}
      />
      <Label htmlFor={name} className={styles.label}>
        {name}
      </Label>
    </div>
  );

  /**
   * render a toggle button for is server side/ no-server side experiment
   * @returns {React.Component} toggle button
   */
  renderIsServerSideField() {
    return this.renderInputCheckboxField(showServerSideField);
  }

  /**
   * render a toggle button for show/no-show components
   * @returns {React.Component} toggle button
   */
  renderNoShowExperimentToggle() {
    return this.renderInputCheckboxField(showNoShowField);
  }

  render() {
    const {
      onNameChange,
      boundOpenComponentForEditing,
      componentDetails,
      isFavorite,
      isReadonly,
      isNewComponent,
      modifyComponent,
      toggleFavoriteContent
    } = this.props;
    const {
      showExperimentComponents,
      showHistory,
      showImageSizeSection,
      isServerSidePopoverOpen
    } = this.state;
    const contentSizes = commonHelper.calculateTotalContentSize(componentDetails);
    const items = get(componentDetails, variantsField.propLocation, []);

    return (
      <div>
        <SectionHeader>Name & Tagging</SectionHeader>
        <NameAndTags
          onNameChange={onNameChange}
          isExperiment
          isFavorite={isFavorite}
          isReadonly={isReadonly}
          onFavoriteClick={toggleFavoriteContent}
          showFavoriteIcon={!isNewComponent}
        />
        <SectionHeader
          onClick={this.toggleExperimentComponentsSection}
          data-test-id={makeId({
            component: this.constructor.name,
            section: 'components'
          })}
        >
          Experiment Components
          {renderArrowStyle(showExperimentComponents)}
        </SectionHeader>
        <Collapse isOpen={showExperimentComponents}>
          <div className="px-4 py-2">
            <div
              className="pt-3 px-3"
              id="isServerSideField"
              onMouseEnter={this.handleHover}
              onMouseLeave={this.handleHoverLeave}
            >
              {this.renderIsServerSideField()}
            </div>
            <Popover
              placement="top"
              isOpen={isServerSidePopoverOpen}
              target="isServerSideField"
              toggle={this.toggle}
            >
              <PopoverBody className={styles.popover}>
                Select to run experiments at the page level (as opposed to component level) to
                bucket ALL users into the experiment
              </PopoverBody>
            </Popover>
            <div className="pt-3 px-3">{this.renderNoShowExperimentToggle()}</div>
            <div className="pb-3 px-3">
              <AttachChildInput
                boundOpenComponentForEditing={boundOpenComponentForEditing}
                isExperimentComponent={isExperiment(componentDetails)}
                isReadonly={isReadonly}
                items={items}
                onChange={this.changeItemsOrder}
                fieldContract={variantsField}
                modifyComponent={modifyComponent}
                updateLocation={variantsField.propLocation}
              />
            </div>
          </div>
        </Collapse>
        <SectionHeader onClick={this.handleWeightSectionClick}>
          Content Weight
          {renderArrowStyle(showImageSizeSection)}
        </SectionHeader>
        <Collapse isOpen={showImageSizeSection}>
          {showImageSizeSection && <ImageSize contentSizes={contentSizes} />}
        </Collapse>
        <SectionHeader onClick={this.toggleHistorySection}>
          Component History
          {renderArrowStyle(showHistory)}
        </SectionHeader>
        <Collapse isOpen={showHistory}>
          <History history={componentDetails.History} />
        </Collapse>
      </div>
    );
  }
}
